/**
 * Node Context Menu Component
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useWorkflowStore } from '@flow-studio/store';
import { useUIStore } from '@flow-studio/store';
import { getNodeDisplayProps } from '@flow-studio/types';
import { ConnectorConfigDialog } from '../Modals/ConnectorConfigDialog';
import { SettingsIcon, ClipboardIcon, TrashIcon } from '@bizfirst/common-react';
import { cloneNode } from '@flow-studio/services';
import { getCanvasMenuBounds, clampMenuPosition } from '../../utils/canvasMenuBounds';

// Pin icon — not in @bizfirst/common-react icon set
const PinIcon = ({ size = 14, color = 'currentColor' }: { size?: number; color?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M12 17v5"/>
    <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76z"/>
  </svg>
);

interface NodeContextMenuProps {
  nodeId: string;
  position: { x: number; y: number };
  onClose: () => void;
  /** Called when the user clicks "Pin Output" — canvas level renders the dialog */
  onPinOutput: (nodeId: string) => void;
  /** Current canvas zoom level — scales the menu so it stays proportional to the
   * (already-zoomed) node it's anchored to, instead of staying a fixed pixel size. */
  zoom?: number;
}

export const NodeContextMenu: React.FC<NodeContextMenuProps> = ({
  nodeId,
  position,
  onClose,
  onPinOutput,
  zoom = 1,
}) => {
  const { deleteNode, updateProcessElement, addNode, nodes, nodeTypes } = useWorkflowStore();
  const { theme } = useUIStore();
  const menuRef = useRef<HTMLDivElement>(null);
  const [isConfigDialogOpen, setIsConfigDialogOpen] = useState(false);
  // null until the first layout measurement — menu renders invisible (but measurable) until then.
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const node = nodes.find((n) => n.id === nodeId);
  const ui = node?.data?.processElement?.configuration?.designer?.ui as any;
  const storedDtId = ui?.dataTemplateID as number | undefined;
  const nodeCode = ui?.nodeType ?? '';
  const nodeTemplate = storedDtId
    ? (nodeTypes.find((nt: any) => nt.id === storedDtId) ?? nodeTypes.find((nt: any) => nt.code === nodeCode))
    : nodeTypes.find((nt: any) => nt.code === nodeCode);
  const acceptedCredentialTypes = (nodeTemplate as any)?.acceptedCredentialTypes
    ?? (node?.data?.processElement?.connector as any)?.configuration?.acceptedCredentialTypes;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // While the Configure dialog is open, keep this menu mounted — the dialog is a child of
      // this component, so unmounting the menu (onClose) would also unmount the dialog before it
      // ever renders. Clicks inside the dialog are "outside" the menu ref and must not close it.
      if (isConfigDialogOpen) return;
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, isConfigDialogOpen]);

  // Clamp the menu to stay fully inside the visible canvas — otherwise a right-click
  // near the canvas edge (as here, since it's anchored to the cursor) can render partly
  // off-screen or behind the side panels / footer.
  useLayoutEffect(() => {
    const menuEl = menuRef.current;
    if (!menuEl) return;
    const menuRect = menuEl.getBoundingClientRect();
    const bounds = getCanvasMenuBounds(menuEl);
    setPos(clampMenuPosition({ top: position.y, left: position.x }, menuRect, bounds));
  }, [position.x, position.y, zoom]);

  const menuStyle: React.CSSProperties = {
    position: 'fixed',
    left: pos ? `${pos.left}px` : `${position.x}px`,
    top: pos ? `${pos.top}px` : `${position.y}px`,
    visibility: pos ? 'visible' : 'hidden',
    transform: `scale(${zoom})`,
    transformOrigin: 'top left',
    background: theme === 'dark'
      ? 'rgba(15, 23, 42, 0.95)'
      : 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(12px)',
    border: theme === 'dark'
      ? '1px solid rgba(58, 140, 69, 0.3)'
      : '1px solid #e5e7eb',
    borderRadius: '12px',
    boxShadow: theme === 'dark'
      ? '0 0 20px rgba(58, 140, 69, 0.4), 0 10px 30px rgba(0, 0, 0, 0.7)'
      : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    minWidth: '200px',
    zIndex: 1000,
    overflow: 'hidden',
    // Hide the menu chrome while the Configure dialog is open (component stays mounted so the
    // dialog survives — see handleClickOutside).
    display: isConfigDialogOpen ? 'none' : undefined,
  };

  const menuItemStyle: React.CSSProperties = {
    padding: '12px 20px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    color: theme === 'dark' ? '#f8fafc' : '#111827',
    borderBottom: theme === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.05)'
      : '1px solid #f3f4f6',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  const handleToggleDisable = () => {
    if (node) {
      updateProcessElement(nodeId, { isDisabled: !node.data.processElement?.isDisabled });
    }
    onClose();
  };

  const handlePinOutput = () => {
    // Delegate to canvas-level handler — NodeContextMenu is about to unmount via onClose()
    onPinOutput(nodeId);
    onClose();
  };

  const handleDelete = () => {
    deleteNode(nodeId);
    onClose();
  };

  const handleDuplicate = () => {
    if (node) {
      addNode(cloneNode(node));
    }
    onClose();
  };

  const handleConfigure = () => {
    // Do NOT call onClose() here — the ConnectorConfigDialog is a child of this component, so
    // unmounting the menu would unmount the dialog immediately. Keep mounted; close both when
    // the dialog itself closes/saves.
    setIsConfigDialogOpen(true);
  };

  const handleConfigDialogClose = () => {
    setIsConfigDialogOpen(false);
    onClose();
  };

  const handleConfigDialogSave = () => {
    setIsConfigDialogOpen(false);
    onClose();
  };

  const hoverBg = theme === 'dark'
    ? 'rgba(58, 140, 69, 0.15)'
    : '#f9fafb';
  const normalBg = 'transparent';
  const deleteHoverBg = theme === 'dark'
    ? 'rgba(244, 63, 94, 0.2)'
    : '#fee2e2';

  const isPinned = !!node?.data.processElement?.pinnedData;

  return (
    <>
    {createPortal(
      <div ref={menuRef} style={menuStyle}>
        <div
          style={menuItemStyle}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = hoverBg; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = normalBg; }}
          onClick={handleToggleDisable}
        >
          {node?.data.processElement?.isDisabled ? '✓ Enable' : '⊗ Disable'}
        </div>
        <div
          style={menuItemStyle}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = hoverBg; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = normalBg; }}
          onClick={handlePinOutput}
        >
          <PinIcon size={14} color={theme === 'dark' ? '#f8fafc' : '#111827'} />
          {isPinned ? 'Edit Pinned Output' : 'Pin Output'}
        </div>
        <div
          style={menuItemStyle}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = hoverBg; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = normalBg; }}
          onClick={handleDuplicate}
        >
          <ClipboardIcon size={14} /> Duplicate
        </div>
        {node && getNodeDisplayProps(node.data).connectorID && (
          <div
            style={menuItemStyle}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = hoverBg; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = normalBg; }}
            onClick={handleConfigure}
          >
            <SettingsIcon size={14} /> Configure
          </div>
        )}
        <div
          style={{ ...menuItemStyle, borderBottom: 'none', color: '#ef4444' }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = deleteHoverBg; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = normalBg; }}
          onClick={handleDelete}
        >
          <TrashIcon size={14} /> Delete
        </div>
      </div>,
      document.body
    )}
    {/* Connector Config Dialog — uses isOpen prop so it renders after menu unmounts */}
    {node && (() => {
      const d = getNodeDisplayProps(node.data);
      return d.connectorID ? (
        <ConnectorConfigDialog
          connectorId={d.connectorID}
          processElementId={node.processElementId ?? d.processElementID}
          isOpen={isConfigDialogOpen}
          onClose={handleConfigDialogClose}
          onSave={handleConfigDialogSave}
          nodeCategory={d.category || ''}
          isTrigger={d.isTrigger}
          triggerType={d.triggerType || ''}
          isManual={d.triggerType === 'manual'}
          acceptedCredentialTypes={acceptedCredentialTypes}
          nodeInfo={{
            id: node.id,
            type: node.type ?? '',
            backendId: node.processElementId ?? d.processElementID,
            data: {
              displayName: d.displayName,
              description: d.description,
              category: d.category || '',
              connectorId: d.connectorID,
              icon: d.icon,
              headerImageUrl: d.headerImageUrl,
            },
          }}
          onNodeInfoSave={(displayName, description) => {
            updateProcessElement(node.id, { displayName, description });
          }}
        />
      ) : null;
    })()}
    </>
  );
};
